import { Page } from '@dixa/lib-shared-habla';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { NotificationType, useNotifications } from 'hooks/useNotifications';

import { FormInfoBanner } from '../../../../components/formInfoBanner';
import { CustomForm } from '../../../../customForms.types';
import { useCustomForms } from '../../../../hooks/useCustomForms/useCustomForms';

import { FormValues } from './editFormSection.types';
import { ActionButtons } from './formActionButtons';
import { FormFields } from './formFields/formFields';
import { FormPreviewModal } from './previewModal';

function getDefaultValues(form: CustomForm | undefined): FormValues {
  return {
    feedbackAfterSubmit: form?.feedbackAfterSubmit ?? '',
    submitButtonLabel: form?.submitButtonLabel ?? '',
    formFields:
      form?.fields.map((field) => ({
        title: field.title,
        placeholder: field.placeholder,
        id: field.id,
        type: field.type,
        required: field.required,
        options: 'options' in field ? field.options : undefined,
      })) ?? [],
  };
}

function formatFields(values: FormValues): CustomForm['fields'] {
  return values.formFields.map((field) => ({
    title: field.title,
    placeholder: field.placeholder,
    id: field.id,
    type: field.type,
    required: field.required,
    options: (field.options?.length ?? 0) > 0 ? field.options : undefined,
  }));
}

type Props = {
  form: CustomForm;
};

export function EditFormSection({ form }: Props): JSX.Element {
  const [isFormPreviewModalOpen, setIsFormPreviewModalOpen] = useState(false);
  const formState = useForm<FormValues>({
    mode: 'all',
    values: getDefaultValues(form),
  });
  const notify = useNotifications();
  const { updateForm } = useCustomForms();

  const onSubmit = async (values: FormValues): Promise<void> => {
    const result = await updateForm({
      id: form.id,
      usage: [], // usage us never updated on client side
      title: form.title,
      fields: formatFields(values),
      submitButtonLabel: values.submitButtonLabel,
      feedbackAfterSubmit: values.feedbackAfterSubmit,
      hasAttributes: values.formFields.length > 0,
    });

    if (result.ok) {
      notify(
        {
          title: 'Form updated',
          time: 5000,
          icon: 'done',
          className: 'success',
        },
        NotificationType.Success,
      );

      return;
    }

    notify(
      {
        title: 'Could not save the form',
        text: result.error,
        icon: 'exclamation',
        className: 'danger',
        time: 5000,
      },
      NotificationType.Error,
    );
  };

  return (
    <FormProvider {...formState}>
      <Page.Section
        title="Edit form"
        controls={
          <ActionButtons
            onPreviewRequest={() => setIsFormPreviewModalOpen(true)}
          />
        }
      >
        <form onSubmit={formState.handleSubmit(onSubmit)}>
          <FormInfoBanner />
          <FormFields />
        </form>

        <FormPreviewModal
          isOpen={isFormPreviewModalOpen}
          onClose={() => setIsFormPreviewModalOpen(false)}
        />
      </Page.Section>
    </FormProvider>
  );
}
